/**
 * Native video compression in the Android app (v2).
 *
 * WHY. The page's own compressor (utils/prepare_video_for_upload.js) reads the
 * whole video into the WebView before it can decide anything, which is the
 * memory ceiling behind probe_failed on big files, and it stops when Android
 * freezes a backgrounded page. The app's VideoCompressor plugin (Media3, in the
 * shell repo Strength-Labs/TurnkeyCoach_Capacitor, docs/VideoCompressor.md)
 * compresses on the phone's hardware encoder instead, and v2 lets the page hand
 * it the picked video BY REFERENCE: the shell already keeps its own copy of
 * every picked video (PickedVideoCache), and `locatePicked` finds that copy from
 * what the page's File reports. The page never reads the video; the output goes
 * straight to the native uploader with the MD5 the plugin computed.
 *
 * WHAT IT ENCODES TO. The same 720p target as the web compressor
 * (UPLOAD_ENCODE_TARGET), and it converts HDR to SDR, which the web compressor
 * cannot do well (Divyam, 2026-10-20; see utils/video_compression_policy.js).
 *
 * THREE OUTCOMES, for upload_video.js's maybeCompressForUpload:
 *   - "compressed": upload `file`, a descriptor of the native output (see
 *     nativeCompressedFile below), by path.
 *   - "declined":   upload the original as it is. A decision about the video or
 *     the device (already efficient, high fps, a failed validation, thermal or
 *     battery, a tone-map the GPU cannot do, a lost frame), which the web
 *     compressor would only reach after reading the whole file, if at all.
 *   - "fallback":   the native path could not even try (no app copy, a probe or
 *     encoder failure). The page's own compressor gets its usual turn.
 *
 * Never throws. Available only in the Android app, on a build whose plugin has
 * locatePicked, with the native background uploader on (the output can only be
 * sent by it) and the `native_video_compression` flag on.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment, capacitorPlatform } from "./detection";
import { isBackgroundUploadAvailable } from "./background_upload";
import {
  SKIP_REASONS,
  COMPRESSION_TIMEOUT,
  UPLOAD_ENCODE_TARGET,
  raceWithTimeout,
  buildCompressionTelemetry,
  validationFailureReason,
  contentExemptionSkipReason,
} from "../utils/video_compression_policy";

const PLUGIN_NAME = "VideoCompressor";

// Where the descriptor keeps what only the native path can use. Never read
// outside this module and upload_video.js's native route.
const NATIVE = "__tkcNativeCompressed";

// The plugin's own timeout, generous because a long clip on a slow encoder can
// take a while, bounded so a stuck encoder cannot hold the upload forever. The
// page waits a little longer than the plugin, so the plugin's own TIMEOUT (which
// also deletes its output) is normally the answer.
const MIN_TIMEOUT_MS = 120_000;
const MAX_TIMEOUT_MS = 30 * 60 * 1000;
const PAGE_TIMEOUT_MARGIN_MS = 15_000;

// Plugin rejection codes that mean "leave this video as it is" rather than
// "let the page's compressor try". See the header for the split. A hot phone
// or one under 15% battery is better off not encoding at all, and the page's
// compressor would only work it harder.
const DECLINE_BY_CODE = Object.freeze({
  HDR_NOT_SUPPORTED: SKIP_REASONS.HDR_EXEMPT, // below Android 10, no tone-mapper
  HDR_TONEMAP_FAILED: SKIP_REASONS.HDR_TONEMAP_FAILED,
  FRAME_RATE_CHANGED: SKIP_REASONS.FRAME_RATE_CHANGED,
  THERMAL_BAIL: SKIP_REASONS.DEVICE_BAIL,
  LOW_BATTERY_BAIL: SKIP_REASONS.DEVICE_BAIL,
  TIMEOUT: SKIP_REASONS.TIMEOUT,
  BUSY: SKIP_REASONS.NATIVE_BUSY,
});
// Battery saver is on most of the time on many Samsung phones, and the page's
// compressor has always run under it there; declining would leave those
// clients with no compression at all, which they have today.
const FALLBACK_BY_CODE = Object.freeze({
  NO_HW_ENCODER: SKIP_REASONS.NO_HW_ENCODER,
  LOW_POWER_BAIL: SKIP_REASONS.DEVICE_BAIL,
});
// Expected answers, not faults: not reported to Honeybadger.
const QUIET_CODES = new Set(["BUSY", "LOW_POWER_BAIL"]);

// Media3's own reason for a failed export, which app builds with shell #32 send
// in the rejection's `data` with COMPRESS_FAILED and HDR_TONEMAP_FAILED. The code
// alone cannot tell a tone-mapping failure from a decoder or encoder one on the
// same HDR video. Empty for older builds and for every other rejection.
function mediaErrorTelemetry(error) {
  const data = error?.data;
  if (typeof data?.mediaErrorName !== "string" || !data.mediaErrorName) {
    return {};
  }
  return {
    native_media_error: data.mediaErrorName,
    // The number too: Media3 names a code it has no entry for "invalid error
    // code", so after a Media3 bump the name alone could lose it.
    ...(Number.isInteger(data.mediaErrorCode)
      ? { native_media_error_code: data.mediaErrorCode }
      : {}),
    ...(typeof data.codecName === "string" && data.codecName
      ? { native_media_codec: data.codecName }
      : {}),
  };
}

function report(error, stage, extra = {}) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_video_compress", stage, ...extra },
    });
  } catch {
    // reporting must never break an upload
  }
}

function getPlugin() {
  try {
    return window.Capacitor?.Plugins?.[PLUGIN_NAME] ?? null;
  } catch {
    return null;
  }
}

// Whether the installed app's plugin really implements `method`. A Capacitor
// plugin object can answer a method name its native build lacks (a
// registerPlugin proxy hands back a callable for any name and only rejects
// UNIMPLEMENTED when it is called), so `typeof plugin.x === "function"` cannot
// be trusted to tell an old app build from a new one. The method list the
// native side registered can.
function pluginHasMethod(method) {
  try {
    const header = window.Capacitor?.PluginHeaders?.find(
      (h) => h.name === PLUGIN_NAME,
    );
    return Boolean(header?.methods?.some((m) => m.name === method));
  } catch {
    return false;
  }
}

/**
 * True when this upload can be compressed natively: the Android app, a build
 * with v2 (locatePicked), the native background uploader on (the only route
 * that can send the output), and the flag on.
 * @returns {boolean}
 */
export function isNativeCompressionAvailable() {
  try {
    if (!isCapacitorEnvironment() || capacitorPlatform() !== "android") {
      return false;
    }
    // Read inline: FlagClassification::Scanner only matches a literal within
    // 80 chars of window._tkcEnabledFeatures.
    const nativeCompressionOn =
      window._tkcEnabledFeatures?.includes("native_video_compression") ?? false;
    const nativeUploadOn =
      window._tkcEnabledFeatures?.includes("native_background_upload") ?? false;
    return (
      nativeCompressionOn &&
      nativeUploadOn &&
      isBackgroundUploadAvailable() &&
      pluginHasMethod("locatePicked") &&
      pluginHasMethod("compress")
    );
  } catch {
    return false;
  }
}

/** Target bitrate for a source frame rate, as the web compressor computes it. */
function targetBitrateFor(fps) {
  const t = UPLOAD_ENCODE_TARGET;
  return fps > t.highFpsThreshold
    ? Math.round(t.h264BitrateBps * t.highFpsBitrateMultiplier)
    : t.h264BitrateBps;
}

function timeoutFor(durationMs) {
  const byLength = (Number(durationMs) || 0) * 3 + 60_000;
  return Math.min(MAX_TIMEOUT_MS, Math.max(MIN_TIMEOUT_MS, byLength));
}

/**
 * What runUpload sends for a native output: enough of a File's shape (name,
 * size, type, lastModified) for its routing and labels, plus the path and MD5
 * only the native route uses. Not a real File: nothing may read it. Named like
 * the web compressor's output (same stem, .mp4).
 */
function nativeCompressedFile(source, out) {
  const name =
    String(source?.name ?? "video").replace(/\.[^./\\]+$/, "") + ".mp4";
  return {
    name,
    size: out.sizeBytes,
    type: "video/mp4",
    lastModified: source?.lastModified ?? Date.now(),
    [NATIVE]: {
      path: out.outputPath,
      uri: `file://${out.outputPath}`,
      md5Base64: out.md5Base64,
    },
  };
}

/** Whether `file` is a native compressor output (see nativeCompressedFile). */
export function isNativeCompressedFile(file) {
  return Boolean(file && file[NATIVE]);
}

/** `{ path, uri, md5Base64 }` of a native output, or null. */
export function nativeCompressedSource(file) {
  return isNativeCompressedFile(file) ? file[NATIVE] : null;
}

/**
 * Deletes a native output once the upload is over, whatever happened. Never
 * throws. The plugin only deletes inside its own output folder, and prunes
 * anything left there after a day.
 */
export async function releaseNativeCompressedFile(file) {
  const source = nativeCompressedSource(file);
  if (!source) {
    return;
  }
  try {
    await getPlugin()?.deleteTemp?.({ path: source.path });
  } catch {
    // best-effort; the plugin's own pruning is the backstop
  }
}

async function subscribeProgress(plugin, onProgress) {
  if (typeof onProgress !== "function") {
    return null;
  }
  try {
    return await plugin.addListener("progress", (event) => {
      const percent = Number(event?.percent);
      if (Number.isFinite(percent)) {
        try {
          onProgress(Math.max(0, Math.min(1, percent / 100)));
        } catch {
          // the caller's handler must never break compression
        }
      }
    });
  } catch {
    return null;
  }
}

// One native compression at a time, queued here. The plugin runs one and
// answers BUSY to a second, and in the app uploads are not serialized by the
// page's upload slot (the native uploader stands it down), so a client who
// picks three videos would otherwise get one compressed and two handed to the
// page's compressor, which reads each whole file into the WebView. Queued, the
// others wait their turn instead. Never rejects, so the chain never breaks.
let queue = Promise.resolve();

/**
 * Compress a picked video natively. See the header for the three outcomes.
 * Waits its turn behind any compression already queued; how long is reported
 * as native_queue_ms.
 *
 * @param {object} args
 * @param {File} args.file the File the user picked (the page never reads it here)
 * @param {(fraction:number)=>void} [args.onProgress] 0..1 while encoding
 * @param {()=>void} [args.onCommit] once every gate has passed, just before
 *   the encode starts; the honest moment to say "Compressing…"
 * @returns {Promise<{outcome:"compressed"|"declined"|"fallback", file?:object, telemetry:object}>}
 */
export function compressPickedVideoNatively(args = {}) {
  const queuedAt = Date.now();
  const run = queue.then(() => compressNow(args, Date.now() - queuedAt));
  queue = run.catch(() => {});
  return run;
}

async function compressNow({ file, onProgress, onCommit } = {}, queuedMs = 0) {
  const startedAt = Date.now();
  const telemetryFor = (compressed, skipReason, probe, out, extra = {}) => ({
    ...buildCompressionTelemetry({
      compressed,
      skipReason,
      startedAt,
      probe,
      out,
    }),
    compressor: "native",
    native_queue_ms: queuedMs,
    ...extra,
  });
  const declined = (reason, probe, out, extra) => ({
    outcome: "declined",
    telemetry: telemetryFor(false, reason, probe, out, extra),
  });
  const fallback = (reason, probe, extra) => ({
    outcome: "fallback",
    telemetry: telemetryFor(false, reason, probe, undefined, extra),
  });

  try {
    const plugin = getPlugin();
    if (!plugin || !file) {
      return fallback(SKIP_REASONS.UNAVAILABLE);
    }

    let path;
    try {
      ({ path } = await plugin.locatePicked({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
      }));
    } catch {
      return fallback(SKIP_REASONS.NOT_LOCATED);
    }
    if (!path) {
      return fallback(SKIP_REASONS.NOT_LOCATED);
    }

    let probe;
    try {
      probe = await plugin.probe({ inputPath: path });
    } catch (error) {
      report(error, "probe");
      return fallback(SKIP_REASONS.PROBE_FAILED);
    }

    const t = UPLOAD_ENCODE_TARGET;
    const targetBitrateBps = targetBitrateFor(probe.fps);
    const exemption = contentExemptionSkipReason({
      probe,
      maxFps: t.maxFps,
      maxDimension: t.maxLongEdge,
      targetBitrateBps,
      convertsHdrToSdr: true,
    });
    if (exemption) {
      return declined(exemption, probe);
    }

    onCommit?.();
    const nativeTimeoutMs = timeoutFor(probe.durationMs);
    const progressHandle = await subscribeProgress(plugin, onProgress);
    let out;
    let work = null;
    try {
      const raced = await raceWithTimeout(
        () =>
          (work = plugin.compress({
            inputPath: path,
            maxDimension: t.maxLongEdge,
            targetBitrateBps,
            preferredCodec: "h264",
            timeoutMs: nativeTimeoutMs,
            toneMapHdr: true,
          })),
        nativeTimeoutMs + PAGE_TIMEOUT_MARGIN_MS,
      );
      if (raced === COMPRESSION_TIMEOUT) {
        // If the encode answers after all, nobody will upload its output.
        Promise.resolve(work)
          .then((late) =>
            late?.outputPath
              ? plugin.deleteTemp({ path: late.outputPath })
              : undefined,
          )
          .catch(() => {});
        try {
          await plugin.cancel();
        } catch {
          // best-effort
        }
        return declined(SKIP_REASONS.TIMEOUT, probe);
      }
      out = raced;
    } catch (error) {
      const code = error?.code;
      const media = mediaErrorTelemetry(error);
      const detail = {
        ...(code ? { native_error_code: code } : {}),
        ...media,
      };
      if (DECLINE_BY_CODE[code]) {
        return declined(DECLINE_BY_CODE[code], probe, undefined, detail);
      }
      if (!QUIET_CODES.has(code)) {
        report(error, "compress", media);
      }
      return fallback(
        FALLBACK_BY_CODE[code] ?? SKIP_REASONS.COMPRESS_FAILED,
        probe,
        detail,
      );
    } finally {
      try {
        await progressHandle?.remove?.();
      } catch {
        // best-effort
      }
    }

    const extra = {
      tone_mapped: out.toneMapped === true,
      native_encoder: out.videoEncoderName ?? null,
      faststart: out.faststart ?? null,
    };
    const failure = validationFailureReason({
      out,
      sizeBytes: file.size,
      probe,
      convertsHdrToSdr: true,
    });
    if (failure || !out.outputPath || !out.md5Base64) {
      try {
        await plugin.deleteTemp({ path: out.outputPath });
      } catch {
        // best-effort
      }
      return declined(
        failure ?? SKIP_REASONS.COMPRESS_FAILED,
        probe,
        out,
        extra,
      );
    }

    return {
      outcome: "compressed",
      file: nativeCompressedFile(file, out),
      telemetry: telemetryFor(true, null, probe, out, extra),
    };
  } catch (error) {
    report(error, "unexpected");
    return fallback(SKIP_REASONS.COMPRESS_FAILED);
  }
}
